import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { MatListItem, MatNavList } from '@angular/material/list';
import { NavigationEnd, Router, RouterLink, UrlTree } from '@angular/router';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';
import { toSignal } from '@angular/core/rxjs-interop';
import { IrisLogoService } from '@iris/common/services/logo.service';
import { SafeUrlPipe } from '@iris/common/modules/pipes-common/pipes';
import { StrakitScrollableComponent } from '@ngx-strakit/cdk/layout/scrollable';
import { filter, take, tap } from 'rxjs/operators';

export type ApplicationSwitcherDisplayMode = 'menu' | 'launchpad';

@Component({
  selector: 'iris-application-switcher',
  imports: [
    MatNavList,
    MatListItem,
    RouterLink,
    SafeUrlPipe,
    StrakitScrollableComponent,
  ],
  templateUrl: './application-switcher.component.html',
  styleUrl: './application-switcher.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.menu-mode]': `displayMode() === 'menu'`,
  },
})
export class IrisApplicationSwitcherComponent {
  private readonly globalSandbox = inject(IrisGlobalSandbox);
  private readonly logoService = inject(IrisLogoService);
  private readonly router = inject(Router);

  readonly brand = toSignal(this.logoService.activeBrand$, {
    initialValue: null,
  });
  readonly brandData = computed(() => this.brand()?.data ?? '');
  readonly brandName = computed(() => this.brand()?.brand ?? '');
  
  readonly displayMode = input<ApplicationSwitcherDisplayMode>('launchpad');

  readonly currentAppAlias = this.globalSandbox.appAlias;
  readonly applications = this.globalSandbox.enabledApplications;

  saveSelectedApp(appAlias: string): void {
    this.globalSandbox.saveSelectedApp(appAlias);
    if (this.displayMode() === 'menu') {
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd),
        filter(event => event.url.includes(this.getApplicationLink(appAlias).splice(1).join('/'))),
        take(1),
        tap(() => window.location.reload()),
      ).subscribe();
    }
  }

  getApplicationLink(appAlias: string): string[] {
    const tree: UrlTree = this.router.parseUrl(window.location.pathname);
    const segmentPaths: string[] = tree.root.children['primary']?.segments.map(segment => segment.path).slice(1) || [];
    return ['/', appAlias, ...segmentPaths];
  }
}
